import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from "react";
import { message, Modal, Typography } from "antd";
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from "../constants/uldConstants";
import { uldService } from "../../../../api/enhancedApi";

const { Text } = Typography;

// 初始状态
const initialState = {
    // 列表相关
    loading: false,
    uldList: [],
    totalUlds: 0,
    page: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    filters: {},
    sortInfo: { sortBy: "uld_identifier", sortOrder: "asc" },

    // 创建/编辑相关
    formVisible: false,
    editingUld: null,
    saving: false,

    // 详情相关
    detailVisible: false,
    detailUld: null,
    detailLoading: false,

    // 标签相关
    tagList: [],
    loadingTags: false,

    // 批量操作相关
    batchOpVisible: false,
    batchOpType: null,
    batchLoading: false,
};

// Action Types
const ACTIONS = {
    SET_LOADING: "SET_LOADING",
    SET_ULD_LIST: "SET_ULD_LIST",
    SET_PAGINATION: "SET_PAGINATION",
    SET_FILTERS: "SET_FILTERS",
    SET_SORT_INFO: "SET_SORT_INFO",
    SET_FORM_VISIBLE: "SET_FORM_VISIBLE",
    SET_EDITING_ULD: "SET_EDITING_ULD",
    SET_SAVING: "SET_SAVING",
    SET_DETAIL_VISIBLE: "SET_DETAIL_VISIBLE",
    SET_DETAIL_ULD: "SET_DETAIL_ULD",
    SET_DETAIL_LOADING: "SET_DETAIL_LOADING",
    SET_TAG_LIST: "SET_TAG_LIST",
    SET_LOADING_TAGS: "SET_LOADING_TAGS",
    SET_BATCH_OP_VISIBLE: "SET_BATCH_OP_VISIBLE",
    SET_BATCH_OP_TYPE: "SET_BATCH_OP_TYPE",
    SET_BATCH_LOADING: "SET_BATCH_LOADING",
};

// Reducer
const reducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_LOADING:
            return { ...state, loading: action.payload };
        case ACTIONS.SET_ULD_LIST:
            return { ...state, uldList: action.payload.items || [], totalUlds: action.payload.total || 0 };
        case ACTIONS.SET_PAGINATION:
            return { ...state, page: action.payload.page, pageSize: action.payload.pageSize };
        case ACTIONS.SET_FILTERS:
            return { ...state, filters: action.payload };
        case ACTIONS.SET_SORT_INFO:
            return { ...state, sortInfo: action.payload };
        case ACTIONS.SET_FORM_VISIBLE:
            return { ...state, formVisible: action.payload };
        case ACTIONS.SET_EDITING_ULD:
            return { ...state, editingUld: action.payload };
        case ACTIONS.SET_SAVING:
            return { ...state, saving: action.payload };
        case ACTIONS.SET_DETAIL_VISIBLE:
            return { ...state, detailVisible: action.payload };
        case ACTIONS.SET_DETAIL_ULD:
            return { ...state, detailUld: action.payload };
        case ACTIONS.SET_DETAIL_LOADING:
            return { ...state, detailLoading: action.payload };
        case ACTIONS.SET_TAG_LIST:
            return { ...state, tagList: action.payload };
        case ACTIONS.SET_LOADING_TAGS:
            return { ...state, loadingTags: action.payload };
        case ACTIONS.SET_BATCH_OP_VISIBLE:
            return { ...state, batchOpVisible: action.payload };
        case ACTIONS.SET_BATCH_OP_TYPE:
            return { ...state, batchOpType: action.payload };
        case ACTIONS.SET_BATCH_LOADING:
            return { ...state, batchLoading: action.payload };
        default:
            return state;
    }
};

// 创建Context
const UldContext = createContext();

// Provider组件
export const UldProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    // 使用ref存储最新的state，避免闭包问题
    const stateRef = useRef(state);
    stateRef.current = state;
    const fetchGenerationRef = useRef(0);

    // 获取ULD列表
    const fetchUlds = useCallback(async () => {
        const generation = ++fetchGenerationRef.current;
        try {
            dispatch({ type: ACTIONS.SET_LOADING, payload: true });

            const currentState = stateRef.current;
            const params = {
                page: currentState.page,
                page_size: currentState.pageSize,
                sort_by: currentState.sortInfo.sortBy,
                sort_order: currentState.sortInfo.sortOrder,
                ...currentState.filters,
            };

            const response = await uldService.listUlds(params);
            if (generation !== fetchGenerationRef.current) {
                return;
            }
            dispatch({ type: ACTIONS.SET_ULD_LIST, payload: response.data });
        } catch (error) {
            if (generation !== fetchGenerationRef.current) {
                return;
            }
            message.error("获取ULD列表失败: " + (error.response?.data?.detail || error.message));
        } finally {
            if (generation === fetchGenerationRef.current) {
                dispatch({ type: ACTIONS.SET_LOADING, payload: false });
            }
        }
    }, []);

    // 获取标签列表
    const fetchTags = useCallback(async () => {
            // 按照API_GUIDELINES规范处理响应格式：{success: true, message: "...", data: [...]}
        try {
            dispatch({ type: ACTIONS.SET_LOADING_TAGS, payload: true });
            const response = await uldService.listTagDefinitions();
            const tagData = response.data.data;
            dispatch({ type: ACTIONS.SET_TAG_LIST, payload: tagData });
        } catch (error) {
            // 权限不足时静默处理（只读用户可能无标签管理权限），不阻断主流程
            if (error.response?.status !== 403) {
                message.error("获取标签列表失败: " + (error.response?.data?.detail || error.message));
            }
        } finally {
            dispatch({ type: ACTIONS.SET_LOADING_TAGS, payload: false });
        }
    }, []);

    // 创建ULD
    const createUld = useCallback(
        async (data) => {
            try {
                dispatch({ type: ACTIONS.SET_SAVING, payload: true });
                const response = await uldService.createUld(data);

                // 按照API_GUIDELINES规范处理响应格式：{success: true, message: "...", data: ...}
                const apiResult = response.data;

                if (data.mode === "single") {
                    message.success(apiResult.message);
                    dispatch({ type: ACTIONS.SET_FORM_VISIBLE, payload: false });
                    fetchUlds();
                } else {
                    // 批量创建返回操作结果 - 优化错误反馈
                    const result = apiResult.data;
                    if (result.success_count === data.uld_identifiers.length) {
                        message.success(`批量创建完成：成功创建 ${result.success_count} 个ULD`);
                    } else if (result.success_count > 0) {
                        Modal.warning({
                            title: "批量创建部分成功",
                            content: (
                                <div>
                                    <p>
                                        ✅ 成功创建 <strong>{result.success_count}</strong> 个ULD
                                    </p>
                                    <p>
                                        ❌ 失败 <strong>{result.failure_count}</strong> 个ULD
                                    </p>
                                    {result.failure_items && result.failure_items.length > 0 && (
                                        <div style={{ marginTop: 12 }}>
                                            <Text strong>失败详情：</Text>
                                            <div
                                                style={{
                                                    marginTop: 8,
                                                    maxHeight: 200,
                                                    overflow: "auto",
                                                    border: "1px solid #d9d9d9",
                                                    borderRadius: 4,
                                                    padding: 8,
                                                    backgroundColor: "#fafafa",
                                                }}
                                            >
                                                {result.failure_items.map((item, index) => (
                                                    <div key={index} style={{ marginBottom: 4 }}>
                                                        <Text code style={{ marginRight: 8 }}>
                                                            {item.identifier}
                                                        </Text>
                                                        <Text type="danger">{item.error}</Text>
                                                    </div>
                                                ))}
                                            </div>
                                            <div style={{ marginTop: 8 }}>
                                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                                    💡 提示：已存在的ULD将被跳过，您可以移除这些识别码后重新创建其他ULD
                                                </Text>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ),
                            width: 500,
                        });
                    } else {
                        Modal.error({
                            title: "批量创建失败",
                            content: (
                                <div>
                                    <p>所有 {result.failure_count} 个ULD创建都失败了</p>
                                    {result.failure_items && result.failure_items.length > 0 && (
                                        <div style={{ marginTop: 12 }}>
                                            <Text strong>失败原因：</Text>
                                            <div
                                                style={{
                                                    marginTop: 8,
                                                    maxHeight: 200,
                                                    overflow: "auto",
                                                    border: "1px solid #d9d9d9",
                                                    borderRadius: 4,
                                                    padding: 8,
                                                    backgroundColor: "#fff2f0",
                                                }}
                                            >
                                                {result.failure_items.map((item, index) => (
                                                    <div key={index} style={{ marginBottom: 4 }}>
                                                        <Text code style={{ marginRight: 8 }}>
                                                            {item.identifier}
                                                        </Text>
                                                        <Text type="danger">{item.error}</Text>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ),
                            width: 500,
                        });
                    }

                    if (result.success_count > 0) {
                        dispatch({ type: ACTIONS.SET_FORM_VISIBLE, payload: false });
                        fetchUlds();
                    }
                }
            } catch (error) {
                message.error("创建ULD失败: " + (error.response?.data?.detail || error.message));
            } finally {
                dispatch({ type: ACTIONS.SET_SAVING, payload: false });
            }
        },
        [], // 移除fetchUlds依赖
    );

    // 更新ULD
    const updateUld = useCallback(
        async (uldId, data) => {
            try {
                dispatch({ type: ACTIONS.SET_SAVING, payload: true });
                const response = await uldService.updateUldMaster(uldId, data);
                // 按照API_GUIDELINES规范处理响应格式
                const apiResult = response.data;
                message.success(apiResult.message);
                dispatch({ type: ACTIONS.SET_FORM_VISIBLE, payload: false });
                dispatch({ type: ACTIONS.SET_EDITING_ULD, payload: null });
                fetchUlds(); // 直接调用，不依赖
            } catch (error) {
                message.error("更新ULD失败: " + (error.response?.data?.detail || error.message));
            } finally {
                dispatch({ type: ACTIONS.SET_SAVING, payload: false });
            }
        },
        [], // 移除fetchUlds依赖
    );

    // 获取ULD详情
    const fetchUldDetail = useCallback(async (uldId) => {
        try {
            dispatch({ type: ACTIONS.SET_DETAIL_LOADING, payload: true });
            const response = await uldService.getUldDetail(uldId);
            dispatch({ type: ACTIONS.SET_DETAIL_ULD, payload: response.data });
        } catch (error) {
            message.error("获取ULD详情失败: " + (error.response?.data?.detail || error.message));
        } finally {
            dispatch({ type: ACTIONS.SET_DETAIL_LOADING, payload: false });
        }
    }, []);

    // 更新筛选条件并刷新列表
    const updateFilters = useCallback(
        (newFilters) => {
            // 使用ref获取最新状态，避免闭包问题
            const currentState = stateRef.current;

            // 如果传入空对象，则完全清空筛选条件
            if (Object.keys(newFilters).length === 0) {
                dispatch({ type: ACTIONS.SET_FILTERS, payload: {} });
                dispatch({ type: ACTIONS.SET_PAGINATION, payload: { page: DEFAULT_PAGE, pageSize: currentState.pageSize } });
                return;
            }

            // 正确处理筛选条件的合并和清除
            const updatedFilters = { ...currentState.filters };

            // 遍历新的筛选条件
            Object.keys(newFilters).forEach((key) => {
                const value = newFilters[key];

                // 如果值为空、null、undefined、空数组或空字符串，则移除该筛选条件
                if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0) || (typeof value === "string" && value.trim() === "")) {
                    delete updatedFilters[key];
                } else {
                    // 否则更新该筛选条件
                    updatedFilters[key] = value;
                }
            });

            dispatch({ type: ACTIONS.SET_FILTERS, payload: updatedFilters });
            dispatch({ type: ACTIONS.SET_PAGINATION, payload: { page: DEFAULT_PAGE, pageSize: currentState.pageSize } });
        },
        [], // 移除state依赖
    );

    // 更新分页参数
    const updatePagination = useCallback((page, pageSize) => {
        dispatch({ type: ACTIONS.SET_PAGINATION, payload: { page, pageSize } });
    }, []);

    // 更新排序
    const updateSortInfo = useCallback((sortInfo) => {
        dispatch({ type: ACTIONS.SET_SORT_INFO, payload: sortInfo });
    }, []);

    // 重置所有筛选条件
    const resetFilters = useCallback(() => {
        // 使用ref获取最新状态，避免闭包问题
        const currentState = stateRef.current;
        dispatch({ type: ACTIONS.SET_FILTERS, payload: {} });
        dispatch({ type: ACTIONS.SET_PAGINATION, payload: { page: DEFAULT_PAGE, pageSize: currentState.pageSize } });
    }, []); // 移除state依赖

    // 打开创建表单
    const showCreateForm = useCallback(() => {
        dispatch({ type: ACTIONS.SET_EDITING_ULD, payload: null });
        dispatch({ type: ACTIONS.SET_FORM_VISIBLE, payload: true });
    }, []);

    // 打开编辑表单
    const showEditForm = useCallback((uldData) => {
        dispatch({ type: ACTIONS.SET_EDITING_ULD, payload: uldData });
        dispatch({ type: ACTIONS.SET_FORM_VISIBLE, payload: true });
    }, []);

    // 关闭表单
    const closeForm = useCallback(() => {
        dispatch({ type: ACTIONS.SET_FORM_VISIBLE, payload: false });
        dispatch({ type: ACTIONS.SET_EDITING_ULD, payload: null });
    }, []);

    // 初始化加载标签（仅挂载时）
    useEffect(() => {
        fetchTags();
    }, []);

    // 当分页、筛选、排序变化时加载列表（含初始挂载）
    useEffect(() => {
        fetchUlds();
    }, [state.page, state.pageSize, state.filters, state.sortInfo]);

    // 上下文值
    const contextValue = {
        ...state,
        fetchUlds,
        fetchTags,
        createUld,
        updateUld,
        fetchUldDetail,
        updateFilters,
        resetFilters,
        updatePagination,
        updateSortInfo,
        showCreateForm,
        showEditForm,
        closeForm,
        dispatch,
        ACTIONS,
    };

    return <UldContext.Provider value={contextValue}>{children}</UldContext.Provider>;
};

// 自定义Hook，用于获取上下文
export const useUld = () => {
    const context = useContext(UldContext);
    if (!context) {
        throw new Error("useUld必须在UldProvider内部使用");
    }
    return context;
};
